// combatpro/panels/monsters/studio/useMonsterStudioEncounterBridge.js
//
// Combat PRO - Monster Studio Encounter Bridge (Stage 62: Test In Encounter &
// Encounter Bridge).
//
// The bridge between Monster Studio and the Stage 61 Encounter Monster Selection
// runtime. It is orchestration ONLY. It owns nothing that the established layers
// already own and recreates none of their logic:
//   - Stage 55 engine    (studioToSelectable) converts the studio model -> selectable.
//   - Stage 61 runtime    (useMonsterSelection: customPool / setCustomPool /
//                          selectMonster / getAllMonsters / selectionVersion) is the
//                          single authoritative custom pool + encounter insertion path.
//   - CombatContext        (switchView / state.currentCombatantIndex) is read, never
//                          modified, for the active slot index and the view switch.
//
// This hook's entire job:
//   1. Test In Encounter  <- viStudioTestInEncounter (battletracker.html 11388):
//      convert the live editor model to a selectable, flag it __temp, add it to the
//      Stage 61 custom pool if no entry matches (__studioId OR name), insert it into
//      the active combatant slot via the Stage 61 selectMonster, switch to the
//      Monsters view, and toast. Non-destructive: the cloud record is never touched,
//      and no save is required - new / edited / duplicated / imported / unsaved
//      monsters all test from the live editor model.
//   2. Custom pool sync   <- viRebuildCustomPool (battletracker.html 10932): whenever
//      the studio library pool changes, rebuild the Stage 61 custom pool = keep the
//      non-studio entries, replace the studio (__custom) ones with reconciled
//      selectables for every non-archived library model. This fires for Save /
//      Save As / Delete / Archive / Restore / Version Restore (all mutate
//      library.customMonsters) and on boot (the initial library load). Import is NOT
//      a rebuild trigger - matching legacy, an imported monster is not in the library
//      until it is saved; Test In Encounter still tests it live in the meantime.
//   3. Ability reconciliation: the studio model exposes UPPERCASE ability keys
//      ({ STR, DEX, ... }); encounter slots and the official MONSTERS_DB use
//      lowercase ({ str, dex, ... }). studioToSelectable copies the uppercase block
//      verbatim, so without reconciliation a studio-origin monster would not populate
//      the encounter ability panel. This bridge lowercases the ability-score keys on
//      every selectable it produces (the minimum fix), so Studio monsters populate
//      the ability panels exactly like official monsters. Both engines stay pure; the
//      mapping lives only here.
//
// Legacy-faithful behaviour preserved (and one inconsistency documented):
//   - The pool add is skipped when an entry already matches by __studioId OR name.
//     Consequence (legacy quirk, preserved): testing an edited but still-pooled
//     monster (same id/name) selects the last-synced pool copy, not the unsaved
//     edits; a brand-new / uniquely-named / not-yet-pooled monster is added and
//     tested with its live edits. Saving first re-syncs the pool (rebuild) so the
//     edits are then reflected.
//
// One necessary React adaptation (documented): legacy pushes to customMonsters
// synchronously and immediately calls selectMonster on the same array. In React
// setCustomPool is an async state update, so a freshly added monster is not yet in
// the Stage 61 pool when selectMonster (a name lookup) runs in the same tick. To keep
// selectMonster as the single authoritative insertion path (Stage 61 is consumed
// unchanged), the actual selection is deferred to an effect keyed on selectionVersion
// that runs once the pool reflects the entry; for an already-pooled monster the pool
// is unchanged and the effect runs immediately on the pending state change.
//
// No CombatContext change. No encounter slot building here (Stage 61 owns it). No DOM,
// no querySelector, no innerHTML, no fetch, no localStorage. No optional chaining.
// No em dash.

import { useState, useEffect, useRef, useCallback, useContext } from 'react';
import { CombatContext } from '../../../contexts/CombatContext';
import useMonsterSelection from '../../../monster/useMonsterSelection';
import { studioToSelectable } from '../../../monster/monsterStudioEngine';

// ---- Pure helpers (exported for tests) -------------------------------------

// Lowercase the six ability-score keys (the minimum reconciliation). Known keys map
// explicitly; any other key falls back to its lowercased form so the function is
// safe for already-lowercase or unexpected input.
export function lowerAbilityScores(scores) {
  var map = { STR: 'str', DEX: 'dex', CON: 'con', INT: 'int', WIS: 'wis', CHA: 'cha' };
  var out = {};
  if (scores && typeof scores === 'object') {
    var keys = Object.keys(scores);
    for (var i = 0; i < keys.length; i++) {
      var k = keys[i];
      var lk = map[k] ? map[k] : String(k).toLowerCase();
      out[lk] = scores[k];
    }
  }
  return out;
}

// Reconcile a studio-origin selectable so the encounter ability panel populates
// exactly like an official monster: copy the selectable and lowercase its
// abilityScores keys. Immutable; everything else is preserved.
export function reconcileSelectable(sel) {
  var s = Object.assign({}, sel);
  s.abilityScores = lowerAbilityScores(sel ? sel.abilityScores : null);
  return s;
}

// Build the Test In Encounter selectable from a studio model: studioToSelectable
// (Stage 55 engine) -> reconcile abilities -> flag __temp (the legacy temp copy).
export function buildTestSelectable(model) {
  var sel = reconcileSelectable(studioToSelectable(model));
  sel.__temp = true;
  return sel;
}

// Rebuild the Stage 61 custom pool from the current selection pool and the studio
// library pool (viRebuildCustomPool). Keep non-studio entries (no __custom flag),
// replace the studio ones with reconciled selectables for non-archived models.
export function rebuildSelectionPool(currentPool, studioPool) {
  var cur = Array.isArray(currentPool) ? currentPool : [];
  var lib = Array.isArray(studioPool) ? studioPool : [];
  var nonStudio = cur.filter(function(m) { return !m || !m.__custom; });
  var mapped = lib.filter(function(m) { return m && !m.archived; }).map(function(m) {
    return reconcileSelectable(studioToSelectable(m));
  });
  return nonStudio.concat(mapped);
}

// ---- Hook ------------------------------------------------------------------

function useMonsterStudioEncounterBridge(props) {
  var p = props || {};
  var editor = p.editor;
  var library = p.library;

  var selection = useMonsterSelection();
  var combat = useContext(CombatContext);

  var messageState = useState(null);
  var message = messageState[0];
  var setMessage = messageState[1];
  var messageIdRef = useRef(0);
  var messageIdState = useState(0);
  var messageId = messageIdState[0];
  var setMessageId = messageIdState[1];

  var pendingState = useState(null);
  var pending = pendingState[0];
  var setPending = pendingState[1];

  function toast(text) {
    messageIdRef.current = messageIdRef.current + 1;
    setMessage(text);
    setMessageId(messageIdRef.current);
  }

  // Latest selection pool, read inside the sync effect without making it a dependency
  // (avoids a self-retrigger loop while still rebuilding from the live pool).
  var currentPoolRef = useRef([]);
  if (selection && Array.isArray(selection.customPool)) {
    currentPoolRef.current = selection.customPool;
  }

  // Custom pool sync (viRebuildCustomPool). Fires whenever the studio library pool
  // changes - Save / Save As / Delete / Archive / Restore / Version Restore and the
  // boot load - rebuilding the Stage 61 custom pool. Import is intentionally not a
  // trigger (the imported model is not in the library until saved), matching legacy.
  var customMonsters = library ? library.customMonsters : null;
  var setCustomPool = selection ? selection.setCustomPool : null;
  useEffect(function() {
    if (typeof setCustomPool !== 'function') { return; }
    var rebuilt = rebuildSelectionPool(currentPoolRef.current, customMonsters);
    setCustomPool(rebuilt);
  }, [customMonsters, setCustomPool]);

  // Test In Encounter (viStudioTestInEncounter). Builds the temp selectable, adds it
  // to the Stage 61 pool when no entry matches by __studioId or name, then records a
  // pending selection; the deferred effect below performs the actual insertion via
  // the Stage 61 selectMonster once the pool reflects the entry.
  var testInEncounter = useCallback(function testInEncounter() {
    if (!editor || !editor.model) { return false; }
    if (!selection || typeof selection.selectMonster !== 'function') { return false; }
    var sel = buildTestSelectable(editor.model);
    var pool = Array.isArray(selection.customPool) ? selection.customPool : [];
    var exists = pool.some(function(m) { return m && (m.__studioId === sel.__studioId || m.name === sel.name); });
    if (!exists && typeof selection.setCustomPool === 'function') {
      selection.setCustomPool(pool.concat([sel]));
    }
    var idx = 0;
    if (combat && combat.state && typeof combat.state.currentCombatantIndex === 'number') {
      idx = combat.state.currentCombatantIndex || 0;
    }
    setPending({ index: idx, name: sel.name });
    return true;
  }, [editor, selection, combat]);

  // Deferred selection: once the pool reflects the pending monster (the version bump
  // after setCustomPool, or immediately for an already-pooled monster), call the
  // Stage 61 selectMonster - the single authoritative insertion path - then switch to
  // the Monsters view and toast, exactly as viStudioTestInEncounter did.
  var selectionVersion = selection ? selection.selectionVersion : 0;
  useEffect(function() {
    if (!pending || !selection || typeof selection.selectMonster !== 'function') { return; }
    var all = (typeof selection.getAllMonsters === 'function') ? selection.getAllMonsters() : [];
    var found = false;
    for (var i = 0; i < all.length; i++) {
      if (all[i] && all[i].name === pending.name) { found = true; break; }
    }
    if (!found) { return; }
    selection.selectMonster(pending.index, pending.name);
    if (combat && typeof combat.switchView === 'function') {
      combat.switchView('view-monsters');
    }
    toast('Test copy loaded into Monster ' + (pending.index + 1) + ' (source untouched)');
    setPending(null);
  }, [pending, selectionVersion, selection, combat]);

  return {
    testInEncounter: testInEncounter,
    message: message,
    messageId: messageId
  };
}

export default useMonsterStudioEncounterBridge;
